import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useMemo, useState } from 'react';
import { startOfDay, subMonths } from 'date-fns';
import { isEqual, keyBy } from 'lodash-es';
import { FilterPicker, RadioButtonWithLabel, KeyboardDatePicker } from 'seagull';
import { SearchFilterType } from '~/components/TableFilters/types';
import { getRecurrenceFilterOptions } from '~/constants';
export { RecurrenceFilterValue } from '~/constants';
import { useIoContext } from '~/contexts';
import { relationshipSummaryModel, timeModel } from '~/models';
export var FilterType = /*#__PURE__*/ function(FilterType) {
    FilterType["AgreementStatus"] = "agreementStatus";
    FilterType["EffectiveDate"] = "effectiveDate";
    FilterType["LastBillingDate"] = "lastBillingDate";
    FilterType["PaymentMethod"] = "paymentMethod";
    FilterType["HasCredit"] = "hasCredit";
    FilterType["Completion"] = "completion";
    FilterType["BillingTrigger"] = "billingTrigger";
    FilterType["Recurrence"] = "recurrence";
    FilterType["Automatic"] = "automatic";
    FilterType["ServiceNames"] = "serviceNames";
    FilterType["ServicePriceIncreaseState"] = "servicePriceIncreaseState";
    FilterType["HasAmendments"] = "hasAmendments";
    return FilterType;
}({});
export var PriceIncreaseFilterValue = /*#__PURE__*/ function(PriceIncreaseFilterValue) {
    PriceIncreaseFilterValue["None"] = "none";
    PriceIncreaseFilterValue["Automatic"] = "automatic";
    return PriceIncreaseFilterValue;
}({});
export const defaultAgreementStatusFilter = [
    relationshipSummaryModel.AgreementStatus.Active,
    relationshipSummaryModel.AgreementStatus.Exhausted
];
export const tableFilterLabels = {
    ["agreementStatus"]: 'Agreement status',
    ["effectiveDate"]: 'Effective date',
    ["lastBillingDate"]: 'Last billing date',
    ["paymentMethod"]: 'Payment method',
    ["hasCredit"]: 'Has credit',
    ["completion"]: 'Service status',
    ["recurrence"]: 'Occurrence',
    ["automatic"]: 'Automatic services state',
    ["billingTrigger"]: 'Billing trigger',
    ["serviceNames"]: 'Service name',
    ["servicePriceIncreaseState"]: 'Automatic price increase',
    ["hasAmendments"]: 'Is amended'
};
export const tableFilterGroups = [
    {
        label: 'Agreements',
        filters: [
            "agreementStatus",
            "effectiveDate",
            "lastBillingDate",
            "paymentMethod",
            "hasCredit",
            "servicePriceIncreaseState",
            "hasAmendments"
        ]
    },
    {
        label: 'Services',
        filters: [
            "serviceNames",
            "completion",
            "billingTrigger",
            "recurrence",
            "automatic"
        ]
    }
];
export function deserializeFiltersFromURL(urlParams) {
    return {
        ["agreementStatus"]: deserializeFilter("agreementStatus", urlParams.agreementStatus),
        ["effectiveDate"]: deserializeFilter("effectiveDate", urlParams.effectiveDate),
        ["lastBillingDate"]: deserializeFilter("lastBillingDate", urlParams.lastBillingDate),
        ["paymentMethod"]: deserializeFilter("paymentMethod", urlParams.paymentMethod),
        ["completion"]: deserializeFilter("completion", urlParams.completion),
        ["recurrence"]: deserializeFilter("recurrence", urlParams.recurrence),
        ["hasCredit"]: deserializeFilter("hasCredit", urlParams.hasCredit),
        ["automatic"]: deserializeFilter("automatic", urlParams.automatic),
        ["billingTrigger"]: deserializeFilter("billingTrigger", urlParams.billingTrigger),
        ["serviceNames"]: deserializeFilter("serviceNames", urlParams.serviceNames),
        ["servicePriceIncreaseState"]: deserializeFilter("servicePriceIncreaseState", urlParams.servicePriceIncreaseState),
        ["hasAmendments"]: deserializeFilter("hasAmendments", urlParams.hasAmendments)
    };
    function deserializeFilter(type, value) {
        const { deserialize } = filtersConfig[type];
        if (typeof value !== 'string') return value;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return deserialize ? deserialize(value) : value;
    }
}
const effectiveDateLabel = 'Effective date';
const lastBillingDateLabel = 'Last billing date';
const agreementStatusFilterOptions = [
    {
        value: relationshipSummaryModel.AgreementStatus.Active,
        label: 'Active'
    },
    {
        value: relationshipSummaryModel.AgreementStatus.Exhausted,
        label: 'Completed'
    },
    {
        value: relationshipSummaryModel.AgreementStatus.Terminated,
        label: 'Terminated'
    }
];
export var HasAmendmentsFilterValue = /*#__PURE__*/ function(HasAmendmentsFilterValue) {
    HasAmendmentsFilterValue["True"] = "true";
    HasAmendmentsFilterValue["False"] = "false";
    return HasAmendmentsFilterValue;
}({});
const hasAmendmentsFilterOptions = [
    {
        value: "true",
        label: 'Yes'
    },
    {
        value: "false",
        label: 'No'
    }
];
export function EffectiveDateFilter(props) {
    const { value, startOpen, onChange, onRemove, onInputBlur, onInputFocus } = props;
    const typedValue = value !== null && value !== void 0 ? value : {};
    const { getNow } = useIoContext();
    const hasDatesFromProps = !!(typedValue.fromDate && typedValue.toDate);
    const [selectingRange, setSelectingRange] = useState(hasDatesFromProps);
    const [fromDate, setFromDate] = useState(getInitialFromDate);
    const [toDate, setToDate] = useState(getInitialToDate);
    useEffect(()=>{
        typedValue.fromDate && setFromDate(typedValue.fromDate);
    }, [
        typedValue.fromDate
    ]);
    useEffect(()=>{
        typedValue.toDate && setToDate(typedValue.toDate);
    }, [
        typedValue.toDate
    ]);
    const label = useMemo(()=>{
        if (typedValue.fromDate && typedValue.toDate) {
            return "".concat(effectiveDateLabel, " (").concat(timeModel.formatDate(typedValue.fromDate), "-").concat(timeModel.formatDate(typedValue.toDate), ")");
        }
        return effectiveDateLabel;
    }, [
        typedValue.fromDate,
        typedValue.toDate
    ]);
    function onChangeIfDifferentValue(newValue) {
        if (!isEqual(newValue, typedValue)) {
            onChange(newValue);
        }
    }
    function getInitialFromDate() {
        return typedValue.fromDate || subMonths(getNow(), 1);
    }
    function getInitialToDate() {
        return typedValue.toDate || getNow();
    }
    return /*#__PURE__*/ _jsxs(FilterPicker, {
        removable: true,
        startOpen: startOpen,
        testId: "effective-date-filter",
        disableApply: selectingRange && fromDate && toDate && fromDate.getTime() > toDate.getTime(),
        label: label,
        title: effectiveDateLabel,
        highlightTrigger: !!(typedValue.fromDate && typedValue.toDate),
        onRemove: onRemove,
        onApply: ()=>onChangeIfDifferentValue(selectingRange ? {
                fromDate,
                toDate
            } : {}),
        onClose: (opts)=>{
            if (opts === null || opts === void 0 ? void 0 : opts.revert) {
                setSelectingRange(hasDatesFromProps);
                setFromDate(getInitialFromDate());
                setToDate(getInitialToDate());
            }
        },
        children: [
            /*#__PURE__*/ _jsx(RadioButtonWithLabel, {
                label: "Select all",
                testId: "effective-date-filter-all",
                checked: !selectingRange,
                onChange: ()=>setSelectingRange(false)
            }),
            /*#__PURE__*/ _jsx(RadioButtonWithLabel, {
                label: "Specific range",
                testId: "effective-date-filter-range",
                checked: selectingRange,
                onChange: ()=>{
                    setSelectingRange(true);
                    const now = startOfDay(getNow());
                    if (!fromDate) {
                        setFromDate(now);
                    }
                    if (!toDate) {
                        setToDate(now);
                    }
                }
            }),
            selectingRange && fromDate && toDate && /*#__PURE__*/ _jsx(DateRangePicker, {
                testId: "effective-date-filter",
                fromDate: fromDate,
                toDate: toDate,
                onChange: (data)=>{
                    setFromDate(data.fromDate);
                    setToDate(data.toDate);
                },
                onInputFocus: onInputFocus,
                onInputBlur: onInputBlur
            })
        ]
    });
}
export var LastBillingDateType = /*#__PURE__*/ function(LastBillingDateType) {
    LastBillingDateType["None"] = "none";
    LastBillingDateType["Never"] = "never";
    LastBillingDateType["Ever"] = "ever";
    LastBillingDateType["Range"] = "range";
    return LastBillingDateType;
}({});
function getLastBillingDateOptionLabel(type) {
    switch(type){
        case "ever":
            return 'Ever billed';
        case "never":
            return 'Never billed';
        case "range":
            return 'Specific range';
        case "none":
            return '';
    }
}
export function LastBillingDateFilter(props) {
    const { value, startOpen, onChange, onRemove, onInputBlur, onInputFocus } = props;
    const typedValue = value !== null && value !== void 0 ? value : {
        type: "none"
    };
    const { getNow } = useIoContext();
    const [type, setType] = useState(typedValue.type);
    const [fromDate, setFromDate] = useState(getInitialFromDate);
    const [toDate, setToDate] = useState(getInitialToDate);
    const disableApply = fromDate && toDate && fromDate.getTime() > toDate.getTime();
    useEffect(()=>{
        setType(typedValue.type);
    }, [
        typedValue.type
    ]);
    useEffect(()=>{
        typedValue.fromDate && setFromDate(typedValue.fromDate);
    }, [
        typedValue.fromDate
    ]);
    useEffect(()=>{
        typedValue.toDate && setToDate(typedValue.toDate);
    }, [
        typedValue.toDate
    ]);
    const label = useMemo(()=>{
        switch(typedValue.type){
            case "ever":
            case "never":
                return "".concat(lastBillingDateLabel, " (").concat(getLastBillingDateOptionLabel(typedValue.type), ")");
            case "range":
                return "".concat(lastBillingDateLabel, " (").concat(timeModel.formatDate(typedValue.fromDate), "-").concat(timeModel.formatDate(typedValue.toDate), ")");
            case "none":
                return lastBillingDateLabel;
        }
    }, [
        typedValue
    ]);
    function onChangeIfDifferentValue(newValue) {
        if (!isEqual(newValue, typedValue)) {
            onChange(newValue);
        }
    }
    function getInitialFromDate() {
        return typedValue.fromDate || subMonths(getNow(), 1);
    }
    function getInitialToDate() {
        return typedValue.toDate || getNow();
    }
    return /*#__PURE__*/ _jsxs(FilterPicker, {
        removable: true,
        startOpen: startOpen,
        testId: "last-billing-date-filter",
        disableApply: disableApply,
        label: label,
        title: lastBillingDateLabel,
        highlightTrigger: typedValue.type !== "none",
        onRemove: onRemove,
        onApply: ()=>{
            if (type === "range") {
                fromDate && toDate && onChangeIfDifferentValue({
                    type,
                    fromDate,
                    toDate
                });
            } else {
                onChangeIfDifferentValue({
                    type
                });
            }
        },
        onClose: (opts)=>{
            if (opts === null || opts === void 0 ? void 0 : opts.revert) {
                setType(value.type);
                setFromDate(getInitialFromDate());
                setToDate(getInitialToDate());
            }
        },
        children: [
            /*#__PURE__*/ _jsx(RadioButtonWithLabel, {
                label: "Select all",
                testId: "last-billing-date-filter-all",
                checked: type === "none",
                onChange: ()=>setType("none")
            }),
            /*#__PURE__*/ _jsx(RadioButtonWithLabel, {
                label: getLastBillingDateOptionLabel("ever"),
                testId: "last-billing-date-filter-ever",
                checked: type === "ever",
                onChange: ()=>setType("ever")
            }),
            /*#__PURE__*/ _jsx(RadioButtonWithLabel, {
                label: getLastBillingDateOptionLabel("never"),
                testId: "last-billing-date-filter-never",
                checked: type === "never",
                onChange: ()=>setType("never")
            }),
            /*#__PURE__*/ _jsx(RadioButtonWithLabel, {
                label: getLastBillingDateOptionLabel("range"),
                testId: "last-billing-date-filter-range",
                checked: type === "range",
                onChange: ()=>{
                    setType("range");
                    const now = startOfDay(getNow());
                    if (!fromDate) {
                        setFromDate(now);
                    }
                    if (!toDate) {
                        setToDate(now);
                    }
                }
            }),
            type === "range" && fromDate && toDate && /*#__PURE__*/ _jsx(DateRangePicker, {
                testId: "last-billing-date-filter",
                fromDate: fromDate,
                toDate: toDate,
                onChange: (data)=>{
                    setFromDate(data.fromDate);
                    setToDate(data.toDate);
                },
                onInputFocus: onInputFocus,
                onInputBlur: onInputBlur
            })
        ]
    });
}
export var PaymentMethodFilterValue = /*#__PURE__*/ function(PaymentMethodFilterValue) {
    PaymentMethodFilterValue["CreditCard"] = "credit";
    PaymentMethodFilterValue["BankAccount"] = "bank";
    PaymentMethodFilterValue["VerificationInitiated"] = "verificationInitiated";
    PaymentMethodFilterValue["VerificationFailed"] = "verificationFailed";
    PaymentMethodFilterValue["NotSet"] = "notSet";
    return PaymentMethodFilterValue;
}({});
const paymentMethodFilterOptions = [
    {
        value: "credit",
        label: 'Card'
    },
    {
        value: "bank",
        label: 'Bank account'
    },
    {
        value: "verificationInitiated",
        label: 'Manual verification initiated'
    },
    {
        value: "verificationFailed",
        label: 'Manual verification failed'
    },
    {
        value: "notSet",
        label: 'Not set'
    }
];
export var CompletionFilterValue = /*#__PURE__*/ function(CompletionFilterValue) {
    CompletionFilterValue["Completed"] = "completed";
    CompletionFilterValue["NotCompleted"] = "notCompleted";
    return CompletionFilterValue;
}({});
const completionFilterOptions = [
    {
        value: "notCompleted",
        label: 'Not completed'
    },
    {
        value: "completed",
        label: 'Completed'
    }
];
export var HasCreditFilterValue = /*#__PURE__*/ function(HasCreditFilterValue) {
    HasCreditFilterValue["Yes"] = "yes";
    HasCreditFilterValue["No"] = "no";
    return HasCreditFilterValue;
}({});
const hasCreditFilterOptions = [
    {
        value: "yes",
        label: 'Yes'
    },
    {
        value: "no",
        label: 'No'
    }
];
export var AutomaticFilterValue = /*#__PURE__*/ function(AutomaticFilterValue) {
    AutomaticFilterValue["Active"] = "active";
    AutomaticFilterValue["Paused"] = "paused";
    return AutomaticFilterValue;
}({});
const automaticFilterOptions = [
    {
        value: "active",
        label: 'Active'
    },
    {
        value: "paused",
        label: 'Paused'
    }
];
export var BillingTriggerFilterValue = /*#__PURE__*/ function(BillingTriggerFilterValue) {
    BillingTriggerFilterValue["Automatic"] = "automatic";
    BillingTriggerFilterValue["Manual"] = "manual";
    return BillingTriggerFilterValue;
}({});
const billingTriggerFilterOptions = [
    {
        value: "automatic",
        label: 'Automatic'
    },
    {
        value: "manual",
        label: 'Manual'
    }
];
function DateRangePicker(props) {
    const { fromDate, toDate, testId, onChange, onInputBlur, onInputFocus } = props;
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx(KeyboardDatePicker, {
                label: "From",
                testId: testId && "".concat(testId, "-from"),
                value: fromDate,
                maxDate: toDate,
                onChange: (date)=>date && onChange && onChange({
                        fromDate: date,
                        toDate
                    }),
                onFocus: onInputFocus,
                onBlur: onInputBlur
            }),
            /*#__PURE__*/ _jsx(KeyboardDatePicker, {
                label: "To",
                testId: testId && "".concat(testId, "-to"),
                value: toDate,
                minDate: fromDate,
                onChange: (date)=>date && onChange && onChange({
                        fromDate,
                        toDate: date
                    }),
                onFocus: onInputFocus,
                onBlur: onInputBlur
            })
        ]
    });
}
const priceIncreaseFilterOptions = [
    {
        value: "automatic",
        label: 'Included'
    },
    {
        value: "none",
        label: 'Not included'
    }
];
const multiSelectLabelsByFilterType = {
    ["agreementStatus"]: keyBy(agreementStatusFilterOptions, 'value'),
    ["paymentMethod"]: keyBy(paymentMethodFilterOptions, 'value'),
    ["hasCredit"]: keyBy(hasCreditFilterOptions, 'value'),
    ["completion"]: keyBy(completionFilterOptions, 'value'),
    ["recurrence"]: keyBy(getRecurrenceFilterOptions({
        onDemand: true
    }), 'value'),
    ["automatic"]: keyBy(automaticFilterOptions, 'value'),
    ["billingTrigger"]: keyBy(billingTriggerFilterOptions, 'value'),
    ["servicePriceIncreaseState"]: keyBy(priceIncreaseFilterOptions, 'value'),
    ["hasAmendments"]: keyBy(hasAmendmentsFilterOptions, 'value')
};
function formatMultiSelectDescription(values, labelByValue) {
    if (values.length === 0) return undefined;
    if (!labelByValue) return values.join(', ');
    const labels = values.map((v)=>{
        var _ref;
        var _labelByValue_v;
        return (_ref = (_labelByValue_v = labelByValue[v]) === null || _labelByValue_v === void 0 ? void 0 : _labelByValue_v.label) !== null && _ref !== void 0 ? _ref : v;
    });
    return labels.join(', ');
}
export function formatFilterDescription(filterType, value) {
    switch(filterType){
        case "effectiveDate":
            {
                const typed = value;
                if (!typed.fromDate || !typed.toDate) return undefined;
                return "".concat(timeModel.formatDate(typed.fromDate), " - ").concat(timeModel.formatDate(typed.toDate));
            }
        case "lastBillingDate":
            {
                const typed = value;
                switch(typed.type){
                    case "ever":
                        return getLastBillingDateOptionLabel("ever");
                    case "never":
                        return getLastBillingDateOptionLabel("never");
                    case "range":
                        return "".concat(timeModel.formatDate(typed.fromDate), " - ").concat(timeModel.formatDate(typed.toDate));
                    case "none":
                        return undefined;
                }
                break;
            }
        case "serviceNames":
            return formatMultiSelectDescription(value);
        default:
            return formatMultiSelectDescription(value, multiSelectLabelsByFilterType[filterType]);
    }
}
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function getSimpleMultiSelectFilterConfig(type) {
    return {
        hasValue (v) {
            return v.length > 0;
        },
        getEmptyValue () {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            return [];
        },
        getRequestParam (v) {
            return {
                [type]: v.length ? v.join(',') : undefined
            };
        }
    };
}
const effectiveDateConfig = {
    hasValue (effectiveDate) {
        return !!(effectiveDate.fromDate && effectiveDate.toDate);
    },
    getEmptyValue () {
        return {};
    },
    getRequestParam (effectiveDate) {
        return {
            fromDate: effectiveDate.fromDate && timeModel.toDatestampString(effectiveDate.fromDate),
            toDate: effectiveDate.toDate && timeModel.toDatestampString(effectiveDate.toDate)
        };
    },
    serialize (param) {
        let { fromDate, toDate } = param;
        return fromDate && toDate ? "".concat(timeModel.toDatestampString(fromDate), ",").concat(timeModel.toDatestampString(toDate)) : '';
    },
    deserialize (str) {
        const splitted = str.split(',');
        return splitted.length === 2 ? {
            fromDate: timeModel.fromDatestampString(splitted[0]),
            toDate: timeModel.fromDatestampString(splitted[1])
        } : {};
    }
};
const lastBillingDateConfig = {
    hasValue (value) {
        return value.type !== "none";
    },
    getEmptyValue () {
        return {
            type: "none"
        };
    },
    getRequestParam (lastBillingDate) {
        const isLastBilledWithinRange = lastBillingDate.type === "range";
        return {
            lastBilled: lastBillingDate.type !== "none" ? lastBillingDate.type : undefined,
            lastBilledFrom: isLastBilledWithinRange ? timeModel.toDatestampString(lastBillingDate.fromDate) : undefined,
            lastBilledTo: isLastBilledWithinRange ? timeModel.toDatestampString(lastBillingDate.toDate) : undefined
        };
    },
    serialize (data) {
        if (data.type === "none") {
            return '';
        }
        if (data.type === "range") {
            return "".concat(timeModel.toDatestampString(data.fromDate), ",").concat(timeModel.toDatestampString(data.toDate));
        }
        return data.type;
    },
    deserialize (lastBilled) {
        if (!lastBilled) {
            return {
                type: "none"
            };
        }
        if (lastBilled === "ever" || lastBilled === "never") {
            return {
                type: lastBilled
            };
        }
        const dates = lastBilled.split(',');
        if (dates.length !== 2) {
            return {
                type: "none"
            };
        }
        const fromDate = timeModel.fromDatestampString(dates[0]);
        const toDate = timeModel.fromDatestampString(dates[1]);
        return {
            type: "range",
            fromDate,
            toDate
        };
    }
};
export const filtersConfig = {
    ["effectiveDate"]: effectiveDateConfig,
    ["lastBillingDate"]: lastBillingDateConfig,
    ["agreementStatus"]: getSimpleMultiSelectFilterConfig("agreementStatus"),
    ["paymentMethod"]: getSimpleMultiSelectFilterConfig("paymentMethod"),
    ["completion"]: getSimpleMultiSelectFilterConfig("completion"),
    ["recurrence"]: getSimpleMultiSelectFilterConfig("recurrence"),
    ["hasCredit"]: getSimpleMultiSelectFilterConfig("hasCredit"),
    ["automatic"]: getSimpleMultiSelectFilterConfig("automatic"),
    ["billingTrigger"]: getSimpleMultiSelectFilterConfig("billingTrigger"),
    ["serviceNames"]: getSimpleMultiSelectFilterConfig("serviceNames"),
    ["servicePriceIncreaseState"]: getSimpleMultiSelectFilterConfig("servicePriceIncreaseState"),
    ["hasAmendments"]: getSimpleMultiSelectFilterConfig("hasAmendments")
};
export function serializeFilter(type, value) {
    const { serialize } = filtersConfig[type];
    return serialize ? serialize(value) : value;
}
export function makeTableFilterConfig(serviceNameOptions, onDemandEnabled) {
    return {
        ["agreementStatus"]: {
            type: SearchFilterType.MultiSelectFilter,
            removable: true,
            options: agreementStatusFilterOptions
        },
        ["effectiveDate"]: {
            type: SearchFilterType.CustomFilter,
            removable: true,
            component: EffectiveDateFilter,
            initialValue: {}
        },
        ["lastBillingDate"]: {
            type: SearchFilterType.CustomFilter,
            removable: true,
            component: LastBillingDateFilter,
            initialValue: {
                type: "none"
            }
        },
        ["paymentMethod"]: {
            type: SearchFilterType.MultiSelectFilter,
            removable: true,
            options: paymentMethodFilterOptions
        },
        ["hasCredit"]: {
            type: SearchFilterType.MultiSelectFilter,
            removable: true,
            options: hasCreditFilterOptions
        },
        ["completion"]: {
            type: SearchFilterType.MultiSelectFilter,
            removable: true,
            options: completionFilterOptions
        },
        ["recurrence"]: {
            type: SearchFilterType.MultiSelectFilter,
            removable: true,
            options: getRecurrenceFilterOptions({
                onDemand: onDemandEnabled
            })
        },
        ["automatic"]: {
            type: SearchFilterType.MultiSelectFilter,
            removable: true,
            options: automaticFilterOptions
        },
        ["billingTrigger"]: {
            type: SearchFilterType.MultiSelectFilter,
            removable: true,
            options: billingTriggerFilterOptions
        },
        ["serviceNames"]: {
            type: SearchFilterType.MultiSelectFilter,
            removable: true,
            withSearchbox: true,
            options: serviceNameOptions
        },
        ["servicePriceIncreaseState"]: {
            type: SearchFilterType.MultiSelectFilter,
            removable: true,
            options: priceIncreaseFilterOptions
        },
        ["hasAmendments"]: {
            type: SearchFilterType.MultiSelectFilter,
            removable: true,
            options: hasAmendmentsFilterOptions
        }
    };
}
